import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsFunnel from 'highcharts/modules/funnel';
import './EmployeeSuccessionDashboard.css';
import TempFilters from "../../components/Filters/TempFilters";
import Header from "../Header";

// Initialize additional Highcharts modules
HighchartsMore(Highcharts);
HighchartsFunnel(Highcharts);

// Sample data for the charts
const sampleData = {
    readiness: [
        { name: 'Ready Now', y: 35, color: '#01A94F' },
        { name: 'Ready in 1-2 Years', y: 45, color: '#085E3A' },
        { name: 'Ready in 3-5 Years', y: 15, color: '#B0D135' },
        { name: 'Not Ready', y: 5, color: '#BC3626' },
    ],
    individualReadiness: [
        { name: 'Skills', value: 80, color: '#01A94F' },
        { name: 'Experience', value: 70, color: '#085E3A' },
        { name: 'Leadership', value: 65, color: '#B0D135' },
        { name: 'Adaptability', value: 60, color: '#08A7DB' },
        { name: 'Strategic Thinking', value: 75, color: '#0C3454' },
    ],
    potentialVsPerformance: [
        { x: 2, y: 3, name: 'John Doe', department: 'Engineering' },
        { x: 4, y: 4, name: 'Jane Smith', department: 'Marketing' },
        { x: 3, y: 2, name: 'Alex Brown', department: 'Finance' },
        { x: 5, y: 5, name: 'Emily Davis', department: 'HR' },
        { x: 1, y: 2, name: 'Michael Lee', department: 'Operations' },
    ],
    leadershipPipeline: [
        { name: 'Individual Contributors', y: 100, color: '#08A7DB' },
        { name: 'Team Leads', y: 60, color: '#B0D135' },
        { name: 'Mid-Level Management', y: 30, color: '#085E3A' },
        { name: 'Senior Leadership', y: 10, color: '#01A94F' },
    ],
    benchStrength: [
        { name: 'CEO', y: 0, color: '#BC3626' },
        { name: 'CFO', y: 1, color: '#01A94F' },
        { name: 'CTO', y: 0, color: '#BC3626' },
        { name: 'VP Operations', y: 2, color: '#085E3A' },
        { name: 'VP Marketing', y: 1, color: '#B0D135' },
    ],
    employeeDevelopment: [
        { name: 'Leadership Training', data: [10, 20, 35, 50, 60], color: '#08A7DB' },
        { name: 'Skill Development', data: [5, 15, 25, 40, 55], color: '#B0D135' },
    ],
    skillGap: [
        {
            name: 'Engineering',
            data: [
                { name: 'Technical Skills', y: 10, color: '#BC3626' },
                { name: 'Leadership Skills', y: 20, color: '#BC3626' },
                { name: 'Soft Skills', y: 15, color: '#BC3626' },
            ],
        },
        {
            name: 'Marketing',
            data: [
                { name: 'Technical Skills', y: 5, color: '#BC3626' },
                { name: 'Leadership Skills', y: 10, color: '#BC3626' },
                { name: 'Soft Skills', y: 25, color: '#BC3626' },
            ],
        },
    ],
    performanceByDepartment: [
        { name: 'Engineering', data: [3.5, 4.0, 4.5, 5.0, 4.2], color: '#01A94F' },
        { name: 'Marketing', data: [3.8, 4.2, 4.8, 5.2, 4.5], color: '#085E3A' },
        { name: 'Finance', data: [3.2, 3.7, 4.0, 4.8, 4.1], color: '#B0D135' },
    ],
};

const EmployeeSuccessionDashboard = () => {
    const [chartData, setChartData] = useState(sampleData);

    // Chart options for Talent Readiness (Pie Chart)
    const readinessChartOptions = {
        chart: { type: 'pie', backgroundColor: 'transparent' },
        title: { text: 'Group Talent Readiness', style: { color: '#0C3454', fontWeight: 'bold' } },
        tooltip: { pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>' },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                    style: { color: '#0C3454' },
                },
            },
        },
        series: [{ name: 'Readiness', data: chartData.readiness }],
        credits: { enabled: false },
    };

    // Chart options for Individual Readiness (Radar Chart)
    const individualReadinessChartOptions = {
        chart: { polar: true, type: 'line', backgroundColor: 'transparent' },
        title: { text: 'Individual Readiness', style: { color: '#0C3454', fontWeight: 'bold' } },
        pane: { size: '80%' },
        xAxis: {
            categories: chartData.individualReadiness.map(item => item.name),
            tickmarkPlacement: 'on',
            lineWidth: 0,
            labels: { style: { color: '#0C3454' } },
        },
        yAxis: {
            gridLineInterpolation: 'polygon',
            lineWidth: 0,
            min: 0,
            max: 100,
            labels: { style: { color: '#0C3454' } },
        },
        tooltip: { pointFormat: '{series.name}: <b>{point.y}%</b>' },
        series: [
            {
                name: 'Readiness',
                data: chartData.individualReadiness.map(item => item.value),
                pointPlacement: 'on',
                color: '#01A94F',
            },
        ],
        credits: { enabled: false },
    };

    // Chart options for Potential vs Performance (Scatter Plot)
    const potentialVsPerformanceOptions = {
        chart: { type: 'scatter', backgroundColor: 'transparent' },
        title: { text: 'Potential vs Performance', style: { color: '#0C3454', fontWeight: 'bold' } },
        xAxis: {
            title: { text: 'Performance', style: { color: '#0C3454' } },
            gridLineColor: '#8C9191',
            min: 0,
            max: 6,
            tickInterval: 1,
        },
        yAxis: {
            title: { text: 'Potential', style: { color: '#0C3454' } },
            gridLineColor: '#8C9191',
            min: 0,
            max: 6,
            tickInterval: 1,
        },
        tooltip: { pointFormat: 'Employee: {point.name}<br>Department: {point.department}' },
        series: [
            {
                name: 'Employees',
                data: chartData.potentialVsPerformance,
                marker: { fillColor: '#08A7DB', radius: 6 },
            },
        ],
        credits: { enabled: false },
    };

    // Chart options for Leadership Pipeline (Funnel Chart)
    const leadershipPipelineChartOptions = {
        chart: { type: 'funnel', backgroundColor: 'transparent' },
        title: { text: 'Leadership Pipeline', style: { color: '#0C3454', fontWeight: 'bold' } },
        plotOptions: {
            series: {
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b> ({point.y})',
                    color: '#0C3454',
                    softConnector: true,
                },
                center: ['40%', '50%'],
                neckWidth: '30%',
                width: '60%',
            },
        },
        tooltip: { pointFormat: '{series.name}: <b>{point.y}</b> employees' },
        series: [
            {
                name: 'Pipeline',
                data: chartData.leadershipPipeline.map(item => ({ name: item.name, y: item.y, color: item.color })),
            },
        ],
        credits: { enabled: false },
    };

    // Chart options for Bench Strength (Bar Chart)
    const benchStrengthChartOptions = {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Bench Strength by Key Role', style: { color: '#0C3454', fontWeight: 'bold' } },
        legend: { enabled: false }, // Disable legend to prevent toggling
        xAxis: { type: 'category', labels: { style: { color: '#0C3454' } } },
        yAxis: {
            title: { text: 'Number of Successors', style: { color: '#0C3454' } },
            gridLineColor: '#8C9191',
            min: 0,
        },
        tooltip: { pointFormat: '{series.name}: <b>{point.y}</b> successor(s)' },
        series: [
            {
                name: 'Successors',
                data: chartData.benchStrength,
                showInLegend: false, // Prevent series from appearing in legend
            },
        ],
        credits: { enabled: false },
    };

    // Chart options for Employee Development (Line Chart)
    const employeeDevelopmentChartOptions = {
        chart: { type: 'line', backgroundColor: 'transparent' },
        title: { text: 'Employee Development Progress', style: { color: '#0C3454', fontWeight: 'bold' } },
        xAxis: {
            categories: ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024', 'Q1 2025'],
            labels: { style: { color: '#0C3454' } },
        },
        yAxis: {
            title: { text: 'Completion Rate (%)', style: { color: '#0C3454' } },
            gridLineColor: '#8C9191',
            min: 0,
            max: 100,
        },
        tooltip: { pointFormat: '{series.name}: <b>{point.y}%</b>' },
        series: chartData.employeeDevelopment,
        credits: { enabled: false },
    };

    // Chart options for Skill Gap Analysis (Stacked Bar Chart)
    const skillGapChartOptions = {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Skill Gap Analysis by Department', style: { color: '#0C3454', fontWeight: 'bold' } },
        xAxis: {
            categories: chartData.skillGap.map(item => item.name),
            labels: { style: { color: '#0C3454' } },
        },
        yAxis: {
            title: { text: 'Number of Employees Lacking Skills', style: { color: '#0C3454' } },
            gridLineColor: '#8C9191',
            min: 0,
            stackLabels: { enabled: true, style: { color: '#0C3454', fontWeight: 'bold' } },
        },
        plotOptions: { series: { stacking: 'normal' } },
        tooltip: { pointFormat: '{series.name}: <b>{point.y}</b> employees' },
        series: [
            {
                name: 'Technical Skills',
                data: chartData.skillGap.map(dept => dept.data.find(skill => skill.name === 'Technical Skills').y),
                color: '#01A94F',
            },
            {
                name: 'Leadership Skills',
                data: chartData.skillGap.map(dept => dept.data.find(skill => skill.name === 'Leadership Skills').y),
                color: '#B0D135',
            },
            {
                name: 'Soft Skills',
                data: chartData.skillGap.map(dept => dept.data.find(skill => skill.name === 'Soft Skills').y),
                color: '#BC3626',
            },
        ],
        credits: { enabled: false },
    };

    // Chart options for Employee Performance (Bar Chart)
    const performanceChartOptions = {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Performance Scores by Department', style: { color: '#0C3454', fontWeight: 'bold' } },
        xAxis: {
            categories: ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024', 'Q1 2025'],
            labels: { style: { color: '#0C3454' } },
        },
        yAxis: {
            title: { text: 'Average Performance Score', style: { color: '#0C3454' } },
            gridLineColor: '#8C9191',
            min: 0,
            max: 5,
        },
        tooltip: { pointFormat: '{series.name}: <b>{point.y}</b>' },
        series: chartData.performanceByDepartment,
        credits: { enabled: false },
    };

    return (
        <>
            <Header icon={"fa-solid fa-users fa-xl mx-2"} label={"People Management/Employee Succession Dashboard"} />
            <main id="main" className="main">
                <TempFilters />
                <section className="section">
                    <div className="row">
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={readinessChartOptions} />
                                    <p className="chart-description">
                                        This pie chart shows the percentage of employees at different readiness levels for succession, helping identify how prepared the workforce is for future leadership roles.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={individualReadinessChartOptions} />
                                    <p className="chart-description">
                                        This radar chart illustrates an individual employee's readiness across key dimensions like skills, experience, and leadership, providing a detailed view of their preparedness for promotion.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={potentialVsPerformanceOptions} />
                                    <p className="chart-description">
                                        This scatter plot maps individual employees' potential against their performance, highlighting top talent and those needing development in specific departments.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={leadershipPipelineChartOptions} />
                                    <p className="chart-description">
                                        This funnel chart depicts the progression of employees through the leadership pipeline, from individual contributors to senior leadership, showing the talent pool at each stage.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={benchStrengthChartOptions} />
                                    <p className="chart-description">
                                        This bar chart shows the number of potential successors for key leadership roles, highlighting positions with strong or weak bench strength.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={employeeDevelopmentChartOptions} />
                                    <p className="chart-description">
                                        This line chart tracks the completion rates of leadership and skill development programs over time, indicating progress in preparing employees for future roles.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={skillGapChartOptions} />
                                    <p className="chart-description">
                                        This stacked bar chart highlights skill gaps across departments, showing the number of employees lacking technical, leadership, and soft skills, guiding targeted development efforts.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <div className="card">
                                <div className="card-body">
                                    <HighchartsReact highcharts={Highcharts} options={performanceChartOptions} />
                                    <p className="chart-description">
                                        This bar chart displays average performance scores by department over time, helping identify high-performing teams and trends in performance improvement.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
};

export default EmployeeSuccessionDashboard;